import { conf, semiProxyBaseUrl } from 'config';
import { HttpService } from './HttpService';
import { httpRequestWrapper } from 'util/request';
import Meeting, {
  Attendee,
  MeetingSession,
  MeetingView,
  Motion,
  UploadResponse,
  Vote,
  Document,
  Reject,
  TechnicalIssue,
  Comment,
  ActivityLog,
} from 'store/types/Meeting';
import ProfileModel from 'store/types/Profile';

export class MeetingService {
  public static getMeetings(): Promise<MeetingView[]> {
    return httpRequestWrapper<MeetingView[]>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static getMeeting(id: string): Promise<MeetingView> {
    return httpRequestWrapper<MeetingView>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${id}`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static createMeeting(meeting: Meeting): Promise<Meeting> {
    return httpRequestWrapper<Meeting>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting, {
          headers: headers,
          data: meeting,
        }),
      requireAuth: true,
    });
  }

  public static updateMeeting(meeting: Meeting): Promise<Meeting> {
    return httpRequestWrapper<Meeting>({
      requestFactory: (headers) =>
        HttpService.put(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting, {
          headers: headers,
          data: meeting,
        }),
      requireAuth: true,
    });
  }

  public static deleteMeeting(meetingId: string): Promise<void> {
    return httpRequestWrapper<void>({
      requestFactory: (headers) =>
        HttpService.delete(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static validateMeetingPassword(meetingId: string, password: string): Promise<boolean> {
    return httpRequestWrapper<boolean>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/validate-password`, {
          headers: headers,
          data: { password },
        }),
      requireAuth: true,
    });
  }

  public static getMeetingSession(meetingId: string): Promise<MeetingSession> {
    return httpRequestWrapper<MeetingSession>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/session`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static saveMeetingSession(session: MeetingSession): Promise<MeetingSession> {
    return httpRequestWrapper<MeetingSession>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/session`, {
          headers: headers,
          data: session,
        }),
      requireAuth: true,
    });
  }

  public static getAttendees(meetingId: string): Promise<Attendee[]> {
    return httpRequestWrapper<Attendee[]>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/attendees`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static getAttendeeProfile(email: string): Promise<ProfileModel> {
    return httpRequestWrapper<ProfileModel>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.semiNetSuite, {
          headers: headers,
          data: {
            method: 'get',
            pathParams: {
              script: conf.netSuite.customerScriptId,
              recordtype: conf.netSuite.customerRecordType,
              email,
            },
          },
        }),
      requireAuth: true,
    });
  }

  public static addAttendee(attendee: Attendee): Promise<Attendee> {
    return httpRequestWrapper<Attendee>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + '/attendees', {
          headers: headers,
          data: attendee,
        }),
      requireAuth: true,
    });
  }

  public static updateAttendee(attendee: Attendee): Promise<Attendee> {
    return httpRequestWrapper<Attendee>({
      requestFactory: (headers) =>
        HttpService.put(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + '/attendees', {
          headers: headers,
          data: attendee,
        }),
      requireAuth: true,
    });
  }

  public static removeAttendee(meetingId: string, attendeeId: string): Promise<void> {
    return httpRequestWrapper<void>({
      requestFactory: (headers) =>
        HttpService.delete(
          semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/attendees/${attendeeId}`,
          {
            headers: headers,
          }
        ),
      requireAuth: true,
    });
  }

  public static setVotingMemberAsync(attendee: Attendee): Promise<Attendee> {
    return httpRequestWrapper<Attendee>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + '/attendees/voting-member', {
          headers: headers,
          data: attendee,
        }),
      requireAuth: true,
    });
  }

  public static getMotions(meetingId: string): Promise<Motion[]> {
    return httpRequestWrapper<Motion[]>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/motions`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static saveMotions(meetingId: string, motions: Motion[]): Promise<Motion[]> {
    return httpRequestWrapper<Motion[]>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/motions`, {
          headers: headers,
          data: motions,
        }),
      requireAuth: true,
    });
  }

  public static getVotes(meetingId: string, motionId: string): Promise<Vote[]> {
    return httpRequestWrapper<Vote[]>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/votes/${motionId}`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static saveVote(vote: Vote): Promise<Vote> {
    return httpRequestWrapper<Vote>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/votes`, {
          headers: headers,
          data: vote,
        }),
      requireAuth: true,
    });
  }

  public static clearVotes(meetingId: string, motionId: string): Promise<void> {
    return httpRequestWrapper<void>({
      requestFactory: (headers) =>
        HttpService.delete(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/votes/${motionId}`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static startVotingTimer(meetingId: string, seconds: number): Promise<MeetingSession> {
    return httpRequestWrapper<MeetingSession>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/votes/timer/start`, {
          headers: headers,
          data: {
            seconds,
            allowReopen: true,
          },
        }),
      requireAuth: true,
    });
  }

  public static evaluateVotingResult(meetingId: string): Promise<MeetingSession> {
    return httpRequestWrapper<MeetingSession>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/votes/result`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static getDocuments(meetingId: string): Promise<Document[]> {
    return httpRequestWrapper<Document[]>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/documents`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static saveDocuments(meetingId: string, documents: Document[]): Promise<Document[]> {
    return httpRequestWrapper<Document[]>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/documents`, {
          headers: headers,
          data: documents,
        }),
      requireAuth: true,
    });
  }

  public static getRejects(meetingId: string): Promise<Reject[]> {
    return httpRequestWrapper<Reject[]>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/rejects`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static saveRejects(meetingId: string, rejects: Reject[]): Promise<Reject[]> {
    return httpRequestWrapper<Reject[]>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/rejects`, {
          headers: headers,
          data: rejects,
        }),
      requireAuth: true,
    });
  }

  public static getTechnicalIssues(meetingId: string): Promise<TechnicalIssue[]> {
    return httpRequestWrapper<TechnicalIssue[]>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/technical-issues`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static saveTechnicalIssues(meetingId: string, technicalIssues: TechnicalIssue[]): Promise<TechnicalIssue[]> {
    return httpRequestWrapper<TechnicalIssue[]>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/technical-issues`, {
          headers: headers,
          data: technicalIssues,
        }),
      requireAuth: true,
    });
  }

  public static getComments(meetingId: string): Promise<Comment[]> {
    return httpRequestWrapper<Comment[]>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/comments`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static saveComments(meetingId: string, comments: Comment[]): Promise<Comment[]> {
    return httpRequestWrapper<Comment[]>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/comments`, {
          headers: headers,
          data: comments,
        }),
      requireAuth: true,
    });
  }

  public static upload(meetingId: string, base64String: string): Promise<UploadResponse> {
    return httpRequestWrapper<UploadResponse>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/upload`, {
          headers: headers,
          data: { base64String },
        }),
      requireAuth: true,
    });
  }

  public static getServerTime(): Promise<string> {
    return httpRequestWrapper<string>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + `/time/utc`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static getActivityLogs(meetingId: string): Promise<ActivityLog[]> {
    return httpRequestWrapper<ActivityLog[]>({
      requestFactory: (headers) =>
        HttpService.get(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/activity-log`, {
          headers: headers,
        }),
      requireAuth: true,
    });
  }

  public static addActivityLog(meetingId: string, activityLog: ActivityLog): Promise<ActivityLog> {
    return httpRequestWrapper<ActivityLog>({
      requestFactory: (headers) =>
        HttpService.post(semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/activity-log`, {
          headers: headers,
          data: activityLog,
        }),
      requireAuth: true,
    });
  }

  public static deleteActivityLog(meetingId: string, activityLogId: string): Promise<void> {
    return httpRequestWrapper<void>({
      requestFactory: (headers) =>
        HttpService.delete(
          semiProxyBaseUrl + conf.proxyEndpoints.virtualMeeting + `/${meetingId}/activity-log/${activityLogId}`,
          {
            headers: headers,
          }
        ),
      requireAuth: true,
    });
  }
}

export default MeetingService;
